import BlockWrapper from "blocks/components/BlockWrapper";
import type { ChatbotBlock as ChatbotBlockType } from "blocks/v2/Chatbot/Chatbot/schema";
import { useTheme } from "lib/theme";
import { Chatbot } from "../Chatbot/Chatbot";
import { trpc } from "utils/trpc";
import { useCallback, useMemo } from "react";
import { usePageContext } from "lib/context/page-context";
import {
  getDatasourceDependencies,
  useContentDatasource,
} from "lib/hooks/useContentDatasource";
import {
  useDraftChatbotBlock,
  useUpdateDraftChatbotBlock,
} from "../DraftChatbotBlockContext";
import { Spinner } from "blocks/components/Spinner";
import { useHandlebarParser } from "lib/hooks/useHandlebarParser";
import { BlockId } from "blocks/types";
import { ChatbotWrapper } from "./ChatbotWrapper";
import { useChatbotProps } from "./useChatbotProps";

type ChatbotBlockProps = {
  block: ChatbotBlockType;
  blockId: BlockId;
};

type ChatbotBlockConfigKeys = keyof ChatbotBlockType["config"];

export function ChatbotBlock(props: ChatbotBlockProps) {
  const supportedDynamicFields: ChatbotBlockConfigKeys[] = useMemo(
    () => ["directive", "defaultPrompt", "defaultMessage", "placeholder"],
    []
  );

  const localBlockId = useMemo(
    () => props.block.config.id,
    [props.block.config.id]
  );

  const isConnectedChatbot = typeof props.block.config.chatbotId === "string";
  const { data: connectedChatbot, isLoading: connectedChatbotIsLoading } =
    trpc.chatbots.get.useQuery(
      { chatbotId: props.block.config.chatbotId as string },
      { enabled: isConnectedChatbot }
    );
  const waitingForRemoteChatbot = useMemo(
    () => isConnectedChatbot && connectedChatbotIsLoading,
    [isConnectedChatbot, connectedChatbotIsLoading]
  );

  const datasourceDependencies = useMemo(
    () => getDatasourceDependencies(props.block.config, supportedDynamicFields),
    [props.block.config, supportedDynamicFields]
  );
  const { data: contentDatasource, externalDatasourcesLoaded } =
    useContentDatasource(datasourceDependencies);

  const publishedConfig = connectedChatbot
    ? connectedChatbot.config
    : props.block.config;

  const draftConfig = useDraftChatbotBlock(localBlockId);
  const inferredConfig = draftConfig ? draftConfig : publishedConfig;

  const config = useHandlebarParser(
    inferredConfig,
    contentDatasource,
    supportedDynamicFields
  );

  const theme = useTheme();

  const { projectId, pageId } = usePageContext();

  const updateDraftChatbotBlock = useUpdateDraftChatbotBlock();

  if (!projectId) throw new Error("Must provide projectId in app context");
  if (!pageId) throw new Error("Must provide pageId in app context");

  const onBlockWrapperClick = useCallback(() => {
    if (localBlockId) {
      updateDraftChatbotBlock(localBlockId, config);
    }
  }, [localBlockId, config, updateDraftChatbotBlock]);

  const isLoading = useMemo(
    () => !externalDatasourcesLoaded || waitingForRemoteChatbot,
    [externalDatasourcesLoaded, waitingForRemoteChatbot]
  );

  const chatbotProps = useChatbotProps({
    blockId: localBlockId,
    config,
  });

  return (
    <BlockWrapper
      blockId={props.blockId}
      block={props.block}
      maxWidth={
        config.style?.width
          ? theme.app.width[config.style?.width]
          : theme.app.width.wide
      }
      alignment={config.style?.alignment}
      onClick={onBlockWrapperClick}
    >
      {isLoading ? (
        <Spinner />
      ) : (
        <ChatbotWrapper>
          <Chatbot {...chatbotProps} />
        </ChatbotWrapper>
      )}
    </BlockWrapper>
  );
}
